import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addBoardingStayUpdate,
  bindBoardingStayQuote,
  createBoardingCashierOrder,
  ensureBoardingStay,
  getBoardingCustomerReport,
  listBoardingStays,
  listBoardingUnits,
  updateBoardingUnitStatus,
  updateBoardingStay,
  upsertBoardingUnit,
} from './boarding-api';
import type { CashierOrder } from '@/features/cashier/types';
import type {
  BoardingCustomerReport,
  BoardingStayDetail,
  BoardingStayInput,
  BoardingStaySummary,
  BoardingStayUpdateInput,
  BoardingStayUpdateEventInput,
  BoardingUnitInput,
  BoardingUnitStatus,
  BoardingUnitSummary,
} from './types';

export const boardingKeys = {
  all: ['boarding'] as const,
  units: (branchId: string) => [...boardingKeys.all, 'units', branchId] as const,
  stays: (branchId: string) => [...boardingKeys.all, 'stays', branchId] as const,
  customerReport: (stayId: string) => [...boardingKeys.all, 'customer-report', stayId] as const,
};

export function useBoardingUnits(branchId: string | undefined) {
  return useQuery<BoardingUnitSummary[]>({
    queryKey: boardingKeys.units(branchId ?? '__none__'),
    queryFn: () => {
      if (!branchId) return Promise.resolve([]);
      return listBoardingUnits(branchId);
    },
    enabled: Boolean(branchId),
  });
}

export function useBoardingStays(branchId: string | undefined) {
  return useQuery<BoardingStaySummary[]>({
    queryKey: boardingKeys.stays(branchId ?? '__none__'),
    queryFn: () => {
      if (!branchId) return Promise.resolve([]);
      return listBoardingStays(branchId);
    },
    enabled: Boolean(branchId),
  });
}

export function useBoardingCustomerReport(stayId: string | undefined) {
  return useQuery<BoardingCustomerReport | null>({
    queryKey: boardingKeys.customerReport(stayId ?? '__none__'),
    queryFn: () => {
      if (!stayId) return Promise.resolve(null);
      return getBoardingCustomerReport(stayId);
    },
    enabled: Boolean(stayId),
  });
}

export function useEnsureBoardingStay(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingStayDetail, Error, {
    bookingId: string;
    input: BoardingStayInput;
  }>({
    mutationFn: ({ bookingId, input }) => ensureBoardingStay(bookingId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.stays(branchId) });
        qc.invalidateQueries({ queryKey: boardingKeys.units(branchId) });
      }
    },
  });
}

export function useUpsertBoardingUnit(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingUnitSummary, Error, BoardingUnitInput>({
    mutationFn: (input) => upsertBoardingUnit(input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.units(branchId) });
      }
    },
  });
}

export function useUpdateBoardingUnitStatus(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingUnitSummary, Error, {
    unitId: string;
    status: BoardingUnitStatus;
  }>({
    mutationFn: ({ unitId, status }) => updateBoardingUnitStatus(unitId, status),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.units(branchId) });
      }
    },
  });
}

export function useUpdateBoardingStay(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingStayDetail, Error, {
    stayId: string;
    input: BoardingStayUpdateInput;
  }>({
    mutationFn: ({ stayId, input }) => updateBoardingStay(stayId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.stays(branchId) });
        qc.invalidateQueries({ queryKey: boardingKeys.units(branchId) });
      }
    },
  });
}

export function useAddBoardingStayUpdate(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingStayDetail, Error, {
    stayId: string;
    input: BoardingStayUpdateEventInput;
  }>({
    mutationFn: ({ stayId, input }) => addBoardingStayUpdate(stayId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.stays(branchId) });
        qc.invalidateQueries({ queryKey: boardingKeys.all });
      }
    },
  });
}

export function useCreateBoardingCashierOrder(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<CashierOrder, Error, { stayId: string }>({
    mutationFn: ({ stayId }) => createBoardingCashierOrder(stayId),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.stays(branchId) });
      }
    },
  });
}

export function useBindBoardingStayQuote(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BoardingStayDetail, Error, { stayId: string }>({
    mutationFn: ({ stayId }) => bindBoardingStayQuote(stayId),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: boardingKeys.stays(branchId) });
        qc.invalidateQueries({ queryKey: boardingKeys.all });
      }
    },
  });
}
